'use client';

import React from 'react';
import { Check, Zap, Building2, ShieldCheck, ArrowRight } from 'lucide-react';
import { formatParticipantLimit, PLANS_CONFIG, type PlanKey } from '@/config/plans.config';

export interface PlanCardData {
  id?: string;
  key: string;
  name: string;
  description?: string;
  platform_fee_percent?: number | null;
  commission_percent?: number | null;
  gst_percent?: number | null;
  price_monthly?: number | null;
  price_yearly?: number | null;
  attendee_limit?: number | null;
  registration_limit?: number | null;
  limits?: {
    participants_max?: number | null;
    campaigns_max?: number | null;
    events_max?: number | null;
  };
  allowed_activity_keys?: string[];
  checklist?: string[];
  features?: Array<{ id?: string | null; key?: string; name: string; category?: string | null }> | string[];
  bundles?: Array<{ id: string; name: string; key?: string }>;
  is_popular?: boolean;
  is_recommended?: boolean;
  is_default?: boolean;
  is_enterprise?: boolean;
  is_active?: boolean;
  display_order?: number;
}

export interface PlanCardProps {
  plan: PlanCardData;
  variant?: 'pricing' | 'settings' | 'admin';
  isCurrent?: boolean;
  onAction?: (plan: PlanCardData) => void;
  actionText?: string;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  actionButton?: React.ReactNode;
  adminActions?: React.ReactNode;
  showCampaignsCard?: boolean;
  campaignsCount?: number | string | null;
}

export function PlanCard({
  plan,
  variant = 'settings',
  isCurrent = false,
  onAction,
  actionText,
  actionDisabled = false,
  actionLoading = false,
  actionButton,
  adminActions,
  showCampaignsCard = false,
  campaignsCount = null,
}: PlanCardProps) {
  const planKey = (plan.key || 'silver').toLowerCase() as PlanKey;
  const config = PLANS_CONFIG[planKey] ?? PLANS_CONFIG.silver;

  const isSilver = planKey === 'silver';
  const isCustom = planKey === 'custom' || Boolean(plan.is_enterprise);
  const isFree = planKey === 'free';
  const isBronze = planKey === 'bronze';

  const feePercent =
    plan.platform_fee_percent != null
      ? Number(plan.platform_fee_percent)
      : plan.commission_percent != null
      ? Number(plan.commission_percent)
      : config.commissionRate;

  const gstPercent = Number(plan.gst_percent ?? 18);

  const participantCap =
    plan.limits?.participants_max ??
    plan.attendee_limit ??
    plan.registration_limit ??
    config.maxParticipantsPerActivity;

  // Key features checklist: always prioritize canonical checklist from plans.config
  const checklist =
    config.checklist && config.checklist.length > 0
      ? config.checklist
      : plan.checklist && plan.checklist.length > 0
      ? plan.checklist
      : [];

  return (
    <div
      className={`relative flex flex-col rounded-3xl border transition-all duration-300 ${
        isCurrent
          ? 'border-emerald-500 bg-white ring-2 ring-emerald-500/20 shadow-xl'
          : isSilver && variant === 'settings'
          ? 'border-slate-800 bg-slate-900 text-white shadow-xl'
          : isSilver && variant === 'pricing'
          ? 'border-[#1C3B2B]/40 bg-white shadow-lg ring-2 ring-[#1C3B2B]/10'
          : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300 shadow-sm'
      } p-5 sm:p-6 flex-1`}
    >
      {/* Top Badge Tag */}
      {isCurrent ? (
        <span className="absolute -top-3 left-6 rounded-full bg-emerald-600 px-3.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-md flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> ACTIVE PLAN
        </span>
      ) : plan.is_popular || plan.is_recommended || (isSilver && variant !== 'admin') ? (
        <span className="absolute -top-3 left-6 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-3.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-md">
          Most Popular
        </span>
      ) : null}

      {/* Plan Header */}
      <div className="mt-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xl font-black tracking-tight">{plan.name} Tier</h3>
          {variant === 'admin' && plan.is_active === false && (
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold text-slate-600">
              Inactive
            </span>
          )}
        </div>
        <p
          className={`mt-1.5 text-xs min-h-[36px] leading-relaxed ${
            isSilver && variant === 'settings' ? 'text-slate-300' : 'text-slate-600'
          }`}
        >
          {plan.description || config.description}
        </p>
      </div>

      {/* Platform Fee Box */}
      <div
        className={`mt-4 rounded-2xl border p-3.5 ${
          isSilver && variant === 'settings'
            ? 'border-slate-800 bg-slate-800/80 text-white'
            : 'border-slate-100 bg-slate-50/80 text-slate-900'
        }`}
      >
        <span
          className={`block text-[10px] font-extrabold uppercase tracking-wider ${
            isSilver && variant === 'settings' ? 'text-emerald-400' : 'text-slate-500'
          }`}
        >
          Platform Fee
        </span>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className="text-2xl font-black tracking-tight">
            {isCustom ? 'Custom' : isFree || feePercent === 0 ? '0.00%' : `${feePercent.toFixed(2)}%`}
          </span>
          {!isCustom && !isFree && feePercent > 0 && (
            <span
              className={`text-xs font-semibold ${
                isSilver && variant === 'settings' ? 'text-slate-300' : 'text-slate-500'
              }`}
            >
              + {gstPercent}% GST
            </span>
          )}
        </div>
        <p
          className={`mt-1 text-[11px] leading-tight ${
            isSilver && variant === 'settings' ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          {isFree || feePercent === 0
            ? 'Free community events · Zero platform fee'
            : isCustom
            ? 'Fully negotiated SLA & volume rates'
            : `${feePercent.toFixed(2)}% platform fee + ${gstPercent}% GST`}
        </p>
      </div>

      {/* Capacity & Campaigns (if needed) */}
      <div className="mt-4 space-y-3">
        <div
          className={`rounded-xl border p-3 ${
            isSilver && variant === 'settings'
              ? 'border-slate-800 bg-slate-800/50'
              : 'border-slate-100 bg-slate-50/50'
          }`}
        >
          <span
            className={`block text-[10px] font-extrabold uppercase tracking-wider ${
              isSilver && variant === 'settings' ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Participants Allowed
          </span>
          <p className="mt-0.5 text-sm font-bold">
            {formatParticipantLimit(participantCap, planKey)}
          </p>
        </div>

        {showCampaignsCard && (
          <div
            className={`rounded-xl border p-3 ${
              isSilver && variant === 'settings'
                ? 'border-slate-800 bg-slate-800/50'
                : 'border-slate-100 bg-slate-50/50'
            }`}
          >
            <span
              className={`block text-[10px] font-extrabold uppercase tracking-wider ${
                isSilver && variant === 'settings' ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Campaigns
            </span>
            <p className="mt-0.5 text-sm font-bold">
              {campaignsCount === 'Flexible'
                ? 'Zero'
                : campaignsCount ?? (isSilver ? '10 per event' : isCustom ? 'Unlimited' : 'Zero')}
            </p>
          </div>
        )}

        {/* Allowed Activities Section */}
        <div className="pt-2 border-t border-slate-100/20">
          <span
            className={`block text-[10px] font-extrabold uppercase tracking-wider mb-1.5 ${
              isSilver && variant === 'settings' ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Allowed Activities
          </span>
          <div className="flex flex-wrap gap-1.5">
            {isFree || isBronze ? (
              <>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
                  Workshops
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
                  Bootcamps
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
                  Seminars
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
                  Quiz
                </span>
              </>
            ) : isSilver ? (
              <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                All Activity Types Included (No Limit)
              </span>
            ) : (
              <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                All 16 Activity Types Included
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Key Features Checklist */}
      <div className="mt-5 flex-1 pt-3 border-t border-slate-100/20">
        <span
          className={`block text-[10px] font-extrabold uppercase tracking-wider mb-2 ${
            isSilver && variant === 'settings' ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          Key Features
        </span>
        <ul className="space-y-2 text-xs">
          {checklist.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 leading-relaxed">
              <Check className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" />
              <span
                className={
                  isSilver && variant === 'settings' ? 'text-slate-200' : 'text-slate-700'
                }
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action / Buttons Slot */}
      <div className="mt-6 pt-4 border-t border-slate-100/20">
        {actionButton ? (
          actionButton
        ) : variant === 'admin' ? (
          adminActions ? (
            <div className="flex flex-wrap gap-2">{adminActions}</div>
          ) : null
        ) : isCurrent ? (
          <button
            type="button"
            disabled
            className="w-full rounded-xl border border-emerald-500 bg-emerald-50 px-4 py-2.5 text-xs font-bold text-emerald-700 cursor-default flex items-center justify-center gap-1.5"
          >
            <ShieldCheck className="w-4 h-4" /> Current Plan
          </button>
        ) : isCustom ? (
          <button
            type="button"
            onClick={() => onAction?.(plan)}
            disabled={actionDisabled || actionLoading}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-slate-800 active:scale-[0.99] transition cursor-pointer disabled:opacity-50"
          >
            <Building2 className="w-3.5 h-3.5" /> Contact Sales
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onAction?.(plan)}
            disabled={actionDisabled || actionLoading}
            className={`w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-bold shadow-sm transition cursor-pointer disabled:opacity-50 ${
              isSilver
                ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {actionText || `Choose ${plan.name}`} <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </button>
        )}
      </div>
    </div>
  );
}
